/**
 * Pure helpers for HATDEV-99973 Add to Home Screen reporting and selection.
 */

/** Fixed Firestore doc id — never auto-id (pairs with CF resolveFixedInlineNotificationId). */
export const ADD_HOME_SCREEN_DOC_ID = "add_home_screen";
export const ADD_HOME_SCREEN_ACTION = "add_to_home_screen";
export const A2HS_DISMISSED_SESSION_KEY = "a2hs-dismissed-session";

export type InlineNotificationCandidate = {
  action?: { details?: string };
  notification_id?: { id?: string };
  expiry_date?: { seconds?: number };
  dismissed?: boolean;
  viewed?: boolean;
  priority?: number;
  device_scoped?: boolean;
  push_pref?: boolean;
  installed_devices?: Array<{ fingerprintId?: string; installed?: boolean }>;
  dismissed_devices?: Array<{ session_id?: string; dismissed?: boolean }>;
  activated_devices?: Array<{ fingerprintId?: string; activated?: boolean }>;
  alt_activated_devices?: Array<{ fingerprintId?: string; activated?: boolean }>;
  alt_dismissed_devices?: Array<{ session_id?: string; dismissed?: boolean }>;
};

export function upsertInstalledDevice(
  existingDevices: Array<{ fingerprintId?: string; installed?: boolean }>,
  fingerprintId: string
): Array<{ fingerprintId: string; installed: boolean }> {
  const others = existingDevices
    .filter(
      (device): device is { fingerprintId: string; installed?: boolean } =>
        !!device?.fingerprintId && device.fingerprintId !== fingerprintId
    )
    .map((device) => ({
      fingerprintId: device.fingerprintId,
      installed: !!device.installed,
    }));

  if (!fingerprintId) {
    return others;
  }

  return [...others, { fingerprintId, installed: true }];
}

export function upsertDismissedDevice(
  existingDevices: Array<{ session_id?: string; dismissed?: boolean }>,
  sessionId: string
): Array<{ session_id: string; dismissed: boolean }> {
  const others = existingDevices
    .filter(
      (device): device is { session_id: string; dismissed?: boolean } =>
        !!device?.session_id && device.session_id !== sessionId
    )
    .map((device) => ({
      session_id: device.session_id,
      dismissed: !!device.dismissed,
    }));

  if (!sessionId) {
    return others;
  }

  return [...others, { session_id: sessionId, dismissed: true }];
}

export const A2HS_VALID_ROUTES = [
  "store",
  "syndicate",
  "game",
  "game-new",
  "games",
  "games-old",
  "voucher",
] as const;

export function isA2hsValidRoute(routeSegment: string | undefined): boolean {
  return !!routeSegment && (A2HS_VALID_ROUTES as readonly string[]).includes(routeSegment);
}

export interface InlineDeviceContext {
  platform: string;
  standalone: boolean;
  fingerprintId: string;
  sessionId: string;
  locallyDismissed?: boolean;
}

/**
 * Resolve OS for login writes + A2HS guards.
 *
 * Mobile UA wins over Capacitor: Device.getInfo() is often empty on iOS Safari,
 * and under Chrome DevTools device mode it can stay on the host OS ("windows")
 * even when navigator.userAgent is already an iPhone/Android string.
 */
export function resolveOperatingSystem(capacitorOs?: string | null): string {
  try {
    const nav = typeof navigator === "undefined" ? undefined : navigator;
    const ua = (nav?.userAgent || "").toLowerCase();
    const maxTouchPoints = nav?.maxTouchPoints || 0;
    // iPadOS 13+ spoofs Macintosh in the UA but still exposes multi-touch.
    if (
      /iphone|ipad|ipod/.test(ua) ||
      (/macintosh|mac os x/.test(ua) && maxTouchPoints > 1)
    ) {
      return "ios";
    }
    if (/android/.test(ua)) {
      return "android";
    }
  } catch {
    // fall through to Capacitor / desktop UA
  }

  const known = (capacitorOs || "").toLowerCase();
  if (
    known === "ios" ||
    known === "android" ||
    known === "windows" ||
    known === "mac"
  ) {
    return known;
  }

  try {
    const nav = typeof navigator === "undefined" ? undefined : navigator;
    const ua = (nav?.userAgent || "").toLowerCase();
    if (/windows/.test(ua)) return "windows";
    if (/macintosh|mac os x/.test(ua)) return "mac";
  } catch {
    // fall through
  }
  return known || "unknown";
}

export function currentInlineDeviceContext(): InlineDeviceContext {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const platform = resolveOperatingSystem();
  let fingerprintId = "";
  let sessionId = "";
  let locallyDismissed = false;
  try {
    fingerprintId = localStorage.getItem("fingerSalt") || "";
    sessionId = sessionStorage.getItem("SessionId") || "";
    locallyDismissed =
      !!sessionId && sessionStorage.getItem(A2HS_DISMISSED_SESSION_KEY) === sessionId;
  } catch {
    // Local eligibility still suppresses standalone when storage is unavailable.
  }
  return {
    platform: platform === "ios" || platform === "android" ? platform : "desktop",
    standalone:
      typeof window !== "undefined" &&
      (!!window.matchMedia?.("(display-mode: standalone)")?.matches ||
        !!(nav as Navigator & { standalone?: boolean })?.standalone),
    fingerprintId,
    sessionId,
    locallyDismissed,
  };
}

export function inlineExpiryMillis(value: unknown): number {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "string") {
    return new Date(value).getTime();
  }
  if (value && typeof (value as { toMillis?: () => number }).toMillis === "function") {
    return (value as { toMillis: () => number }).toMillis();
  }
  const seconds = (value as { seconds?: number; _seconds?: number })?.seconds
    ?? (value as { _seconds?: number })?._seconds;
  return Number(seconds) * 1000;
}

/**
 * Client guard for AC4/AC5: only iOS Safari (browser) may render the A2HS banner.
 * Standalone, Android, and desktop must never show it even if main_pages is stale.
 */
export function shouldShowAddHomeScreenBanner(
  actionDetails: string | undefined,
  operatingSystem: string | undefined,
  isStandalone: boolean
): boolean {
  const details = typeof actionDetails === "string" ? actionDetails.toLowerCase() : undefined;
  if (details !== ADD_HOME_SCREEN_ACTION) {
    return false;
  }
  return resolveOperatingSystem(operatingSystem) === "ios" && !isStandalone;
}

/** True when platform/display_mode changed and ranking must be re-triggered. */
export function didA2hsLoginContextChange(
  previousPlatform: string | undefined,
  previousDisplayMode: string | undefined,
  nextPlatform: string,
  nextDisplayMode: string
): boolean {
  return (
    (previousPlatform || "") !== nextPlatform || (previousDisplayMode || "") !== nextDisplayMode
  );
}

export function isAddHomeScreenInline(
  notification: InlineNotificationCandidate | null | undefined
): boolean {
  const details =
    typeof notification?.action?.details === "string"
      ? notification.action.details.toLowerCase()
      : "";
  const id = notification?.notification_id?.id;
  return details === ADD_HOME_SCREEN_ACTION || id === ADD_HOME_SCREEN_DOC_ID;
}

/** Select from account-neutral candidates using this browser's identity only. */
export function selectDeviceInlineNotification<T extends Record<string, unknown>>(
  candidates: T[] | undefined,
  context: InlineDeviceContext = currentInlineDeviceContext(),
  now = Date.now()
): T | null {
  const iosBrowser = context.platform === "ios" && !context.standalone;
  const eligible = (Array.isArray(candidates) ? candidates : []).filter((notification) => {
    const id = (notification?.["notification_id"] as { id?: string } | undefined)?.id;
    if (!id || !(inlineExpiryMillis(notification["expiry_date"]) > now)) {
      return false;
    }
    if (id === ADD_HOME_SCREEN_DOC_ID) {
      const installed = notification["installed_devices"] as
        | Array<{ fingerprintId?: string; installed?: boolean }>
        | undefined;
      const dismissed = notification["dismissed_devices"] as
        | Array<{ session_id?: string; dismissed?: boolean }>
        | undefined;
      return (
        iosBrowser &&
        !context.locallyDismissed &&
        !installed?.some(
          (device) => device?.fingerprintId === context.fingerprintId && device?.installed
        ) &&
        !dismissed?.some(
          (device) => device?.session_id === context.sessionId && device?.dismissed
        ) &&
        !notification["dismissed"] &&
        !notification["viewed"]
      );
    }
    if (id === "push_notification") {
      if (iosBrowser) {
        return false;
      }
      if (notification["device_scoped"]) {
        const primary = notification["push_pref"] !== false;
        const activated = (
          primary ? notification["activated_devices"] : notification["alt_activated_devices"]
        ) as Array<{ fingerprintId?: string; activated?: boolean }> | undefined;
        const dismissed = (
          primary ? notification["dismissed_devices"] : notification["alt_dismissed_devices"]
        ) as Array<{ session_id?: string; dismissed?: boolean }> | undefined;
        return (
          !activated?.some(
            (device) => device?.fingerprintId === context.fingerprintId && device?.activated
          ) &&
          !dismissed?.some(
            (device) => device?.session_id === context.sessionId && device?.dismissed
          )
        );
      }
    }
    return !notification["dismissed"] && !notification["viewed"];
  });
  return (
    eligible.find(
      (notification) =>
        (notification["notification_id"] as { id?: string })?.id === ADD_HOME_SCREEN_DOC_ID
    ) ||
    eligible[0] ||
    null
  );
}

/**
 * Legacy fallback when only the single-winner `inline_notifications` array exists.
 * Prefer `selectDeviceInlineNotification` against `inline_notification_candidates`.
 */
export function selectPreferredInlineNotification<T extends InlineNotificationCandidate>(
  inlineNotifications: T[] | undefined,
  options: {
    operatingSystem?: string;
    isStandalone: boolean;
    nowMs?: number;
  }
): T | null {
  return selectDeviceInlineNotification(
    inlineNotifications as Array<T & Record<string, unknown>> | undefined,
    {
      platform: options.operatingSystem || "desktop",
      standalone: options.isStandalone,
      fingerprintId: "",
      sessionId: "",
      locallyDismissed: false,
    },
    options.nowMs
  );
}

/** Resolve candidates-first, then legacy winner array. */
export function resolveInlineCandidateList(pageDetails: {
  inline_notification_candidates?: unknown;
  inline_notifications?: unknown;
} | null | undefined): Array<Record<string, unknown>> | undefined {
  const candidates = pageDetails?.inline_notification_candidates;
  if (Array.isArray(candidates) && candidates.length > 0) {
    return candidates as Array<Record<string, unknown>>;
  }
  const legacy = pageDetails?.inline_notifications;
  return Array.isArray(legacy) ? (legacy as Array<Record<string, unknown>>) : undefined;
}
